import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { productionReadinessApi } from "../../services/productionReadinessApi";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { ReadinessCategoryCard } from "../../components/ReadinessCategoryCard";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { formatJst } from "../../lib/date";
import { readinessStatusClass, overallReadinessClass } from "../../lib/jobStatusFormat";
import { useLocalization } from "../../hooks/useLocalization";

export default function ProductionReadinessPage() {
  const { t } = useLocalization();
  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  const load = () => {
    setLoading(true);
    setError(false);
    productionReadinessApi.get()
      .then((r) => setData(r.data))
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); else setError(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const handle = () => load();
    window.addEventListener("yoko:refresh-route", handle);
    return () => window.removeEventListener("yoko:refresh-route", handle);
  }, []);

  if (accessDenied) return <AccessDenied resourceLabel={t("nav.productionReadiness")} />;

  return (
    <div data-testid="production-readiness-page">
      <PageHelpBanner pageKey="production-readiness" title={t("pageHelp.defaultTitle")}>
        <p>{t("productionReadiness.help")}</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("productionReadiness.title")}</h2>
        <Button variant="outline" size="sm" data-testid="production-readiness-refresh" onClick={load}>
          <RefreshCw size={14} className="mr-1.5" /> {t("common.refresh")}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">{t("common.loading")}</p>
      ) : error ? (
        <div className="text-center py-8" data-testid="production-readiness-error">
          <p className="text-sm text-gray-500 mb-2">{t("productionReadiness.loadError")}</p>
          <button className="text-sm text-blue-600 underline" data-testid="production-readiness-retry" onClick={load}>{t("common.retry")}</button>
        </div>
      ) : (
        <>
          <div className={`rounded-xl border-2 p-5 mb-6 ${overallReadinessClass(data.overall_status)}`} data-testid="readiness-overall-status">
            <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{t("productionReadiness.overallStatus")}</p>
            <p className="text-2xl font-bold mt-1" data-testid="readiness-overall-status-label">{t(`overallReadiness.${data.overall_status}`)}</p>
            <p className="text-xs mt-2 opacity-70">
              {t("productionReadiness.environment")}: {data.environment} · {t("productionReadiness.lastChecked")}: {formatJst(data.generated_at)}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6" data-testid="readiness-integrations-summary">
            <h3 className="text-sm font-bold text-gray-800 mb-3">{t("productionReadiness.externalIntegrations")}</h3>
            <div className="flex flex-wrap gap-2">
              {data.integration_checks.map((check) => (
                <Badge key={check.name} className={readinessStatusClass(check.status)} data-testid={`integration-badge-${check.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {check.name}: {t(`readinessStatus.${check.status}`)}
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.categories.map((category) => (
              <ReadinessCategoryCard key={category.key} category={category} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
